import { ChangeDetectorRef, Component, Inject } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { IDialogData } from "../../viewer-components/interfaces/i-dialog-data";
import { FileReaderService } from "../../viewer-components/services/readers/file-reader.service";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { FeatureCollection } from "geojson";

@Component({
  selector: "app-upload-selection-file-dialog",
  templateUrl: "./upload-selection-file-dialog.component.html",
  styleUrls: ["./upload-selection-file-dialog.component.scss"],
  providers: [MatDialogConfig],
  standalone: false,
})
export class UploadSelectionFileDialogComponent {
  isDragOver = false;

  fileIsValid = false;

  isBoundaryValid = false;

  feature = null;

  fileName = "";

  errorMessage = "";

  boundaryType = "";

  form = new FormGroup({
    wkt: new FormControl(""),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDialogData,
    protected matDialogRef: MatDialogRef<UploadSelectionFileDialogComponent>,
    protected fileReaderService: FileReaderService,
    protected cdr: ChangeDetectorRef,
  ) {}

  onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.fileName =
      event &&
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0] &&
      event.dataTransfer.files[0].name;

    this.readFiles(event.dataTransfer.files[0]);

    this.isDragOver = false;
  }

  onDragOver(evt) {
    this.isDragOver = true;
    evt.preventDefault();
    evt.stopPropagation();
  }

  onDragLeave(evt) {
    this.isDragOver = false;
    evt.preventDefault();
    evt.stopPropagation();
  }

  onChangeDataType() {
    this.feature = null;
    this.isBoundaryValid = false;
    this.form.controls.wkt.setValue("");
  }

  /**
   * Opens file selector and processes selected files.
   */
  selectFile(): void {
    const fileInput = document.getElementById("uploadFile") as HTMLInputElement;
    if (fileInput) fileInput.click();
  }

  /**
   * Handles file input change event from the hidden input element.
   * @param event Event
   */
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileIsValid = true;
      this.errorMessage = "";
      this.fileName = input.files[0].name;

      this.readFiles(input.files[0]);
    }
    // Reset input value to allow selecting the same file again
    input.value = "";
  }

  readFiles(file: any) {
    const extension = FileReaderService.getFileExtension(file.name);
    if (file) {
      if (extension === "wkt" || extension === "geojson") {
        this.readFeatureFile(file, extension);
      } else {
        this.fileReaderService.readFile(file).subscribe(
          (result) => {
            //Check geojson content
            const geometryCollection = result.data;
            this.isBoundaryValid = this.checkBoundaryValid(geometryCollection);
            this.cdr.detectChanges();
          },
          (error) => {
            this.errorMessage = error;
            this.fileIsValid = false;
            this.cdr.detectChanges();
          },
        );
      }
    }
  }

  readFeatureFile(file, extension) {
    this.fileName = file.name;
    const reader = new FileReader();
    this.fileIsValid = false;
    this.errorMessage = null;
    reader.addEventListener("load", (event) => {
      if (extension === "wkt") {
        this.checkWKT(event.target.result.toString());
      } else {
        this.checkGeoJSON(event.target.result.toString());
      }
      this.cdr.detectChanges();
      if (this.feature) {
        this.addFeature();
      }
    });
    reader.readAsText(file);
  }

  checkText() {
    if (this.boundaryType === "WKT") {
      this.checkWKT();
    } else {
      this.checkGeoJSON();
    }
  }

  /**
   * Centralized error handler for boundary upload errors.
   * Sets feature to null, fileIsValid to false, errorMessage, and isBoundaryValid to false.
   * @param message Error message to display
   */
  handleError(message: string): void {
    this.feature = null;
    this.fileIsValid = false;
    this.errorMessage = message;
    this.isBoundaryValid = false;
  }

  checkGeoJSON(geoJson: string = null) {
    this.feature = null;
    const input = geoJson || this.form.controls.wkt.value;
    if (input != null && input.trim().length > 0) {
      try {
        const featureCollection = JSON.parse(input);
        this.handleFeatureCollectionContent(featureCollection);
      } catch (err) {
        this.handleError("Oops! The GEOJSON data is not valid");
      }
    }
  }

  checkWKT(wkt: string = null) {
    this.fileIsValid = false;
    this.errorMessage = "";
    const input = wkt ? wkt : this.form.controls.wkt.value;
    if (input != null && input.trim().length > 0) {
      try {
        const featureCollection = GeoService.wktToFeatureCollection(input, false);
        this.handleFeatureCollectionContent(featureCollection);
      } catch (err) {
        this.handleError("Oops! The WKT data is not valid");
      }
    }
  }

  handleFeatureCollectionContent(featureCollection: FeatureCollection<any>): any {
    try {
      const normalization = this.fileReaderService.prepareGeoJson(featureCollection, "content-data");
      if (!normalization || !normalization?.data) {
        this.handleError("Oops! The data content is not valid");
      } else {
        this.isBoundaryValid = this.validateBoundaryGeometry(normalization);
        if (this.isBoundaryValid) {
          const fc = normalization.data;
          this.feature = fc.features[0];
        } else {
          this.handleError(this.errorMessage || "Oops! The boundary is not valid");
        }
      }
    } catch (err) {
      this.handleError("Oops! The data content is not valid");
    }
    return this.feature;
  }

  /**
   * Validates that the normalization result contains exactly one Polygon feature.
   * Sets errorMessage if invalid.
   * @param normalization NormalizationResult from FileReaderService
   * @returns boolean true if valid, false otherwise
   */
  validateBoundaryGeometry(normalization: any): boolean {
    if (!normalization || !normalization.data || !normalization.data.features) {
      this.errorMessage = "No valid geometry found in the uploaded file.";
      return false;
    }
    const featureCollection = normalization.data;
    const nonPolygon = featureCollection.features.find(
      (feat) => feat.geometry.type !== ViewerConstants.GEOMETRY_TYPE.Polygon,
    );
    if (nonPolygon) {
      this.errorMessage =
        "The boundary you selected contains a " +
        nonPolygon.geometry.type +
        ". Import a boundary that contains valid shapes.";
      return false;
    }
    if (featureCollection.features.length !== 1) {
      this.errorMessage =
        "The boundary you selected contains multiple polygons. Import a boundary that contains a single polygon.";
      return false;
    }
    this.feature = featureCollection.features[0];
    this.feature.properties.epsg = ViewerConstants.PROJECTIONS.WebMercator;
    return true;
  }

  checkBoundaryValid(featureCollection, addFeature = true, isGeoJSON = false): boolean {
    if (featureCollection?.features.some((feat) => feat.geometry.type !== ViewerConstants.GEOMETRY_TYPE.Polygon)) {
      const geometryType = featureCollection?.features.find(
        (feat) => feat.geometry.type !== ViewerConstants.GEOMETRY_TYPE.Polygon,
      ).geometry.type;
      if (geometryType) {
        this.errorMessage =
          "The boundary you selected contains a " + geometryType + ". Import a boundary that contains valid shapes.";
      }
      return false;
    } else if (featureCollection?.features && featureCollection?.features.length === 1) {
      this.feature = featureCollection?.features[0];
      if (isGeoJSON) {
        this.feature.properties.epsg = ViewerConstants.PROJECTIONS.WGS84;
      }
      if (addFeature) {
        this.addFeature();
      }
      return true;
    } else {
      this.errorMessage =
        "The boundary you selected contains multiple polygons. Import a boundary that contains a single polygon.";
      return false;
    }
  }

  addFeature() {
    this.matDialogRef.close(this.feature);
  }

  closeDialog() {
    this.matDialogRef.close();
  }
}
